import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import AppLayout from '../../components/AppLayout';
import LoadingSpinner from '../../components/LoadingSpinner';

import BulkOperationDetailsReceipts from './components/BulkOperationDetailsReceipts';
import BulkOperationDetailsHeader from './components/ImportDetailsHeader';
import { useBulkOperationMetadata } from './hooks/useImportMetadata';
import { useBulkOperationQuery } from './hooks/useImportQuery';
import { useReceiptCollection } from './hooks/useReceiptCollection';
import { useUploadedReceiptsQuery } from './hooks/useUploadedReceiptsQuery';

const BulkOperationDetailsPage: React.FC = () => {
  const { bulkOperationId } = useParams<{ bulkOperationId: string }>();
  const navigate = useNavigate();
  const [hasUploadingFiles, setHasUploadingFiles] = useState(false);

  const { bulkOperation, loading, error } = useBulkOperationQuery(
    bulkOperationId || ''
  );

  // Fetch receipts based on the bulk operation's receiptIds
  const { receipts, loading: receiptsLoading } = useUploadedReceiptsQuery(
    bulkOperation?.receiptIds || []
  );

  // Update page title when bulk operation is loaded
  useEffect(() => {
    if (bulkOperation?.title) {
      document.title = `${bulkOperation.title} - Acconto`;
    } else {
      document.title = 'Bulk Upload Details - Acconto';
    }
  }, [bulkOperation?.title]);

  const { updateBulkOperation, isUpdating } = useBulkOperationMetadata(
    bulkOperationId || ''
  );
  const { removeReceipt, removingReceiptIds } = useReceiptCollection(
    bulkOperationId || ''
  );

  // Loading state
  if (loading) {
    return (
      <AppLayout>
        <div className='flex items-center justify-center h-full p-4'>
          <div className='text-center'>
            <LoadingSpinner size='xl' className='mx-auto mb-4' />
            <p className='text-gray-600 dark:text-gray-400'>
              Loading details...
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Error state
  if (error || !bulkOperation) {
    return (
      <AppLayout>
        <div className='flex items-center justify-center h-full p-4'>
          <div className='text-center'>
            <div className='text-xl font-semibold text-red-600 mb-2'>
              Error loading bulk upload
            </div>
            <p className='text-red-500 mb-4'>
              {error || 'Bulk upload not found'}
            </p>
            <button
              onClick={() => navigate('/bulk-upload')}
              className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200'
            >
              Back to Bulk Uploads
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className='p-4'>
        <div className='max-w-7xl mx-auto'>
          {/* Back Button */}
          <button
            onClick={() => navigate('/bulk-upload')}
            className='flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4 transition-colors duration-200'
          >
            <ArrowLeftIcon className='w-5 h-5 mr-2' />
            Back to Bulk Uploads
          </button>

          {/* Bulk Operation Details Header */}
          <div className='mb-6'>
            <BulkOperationDetailsHeader
              title={bulkOperation.title}
              description={bulkOperation.description}
              createdAt={bulkOperation.createdAt}
              updatedAt={bulkOperation.updatedAt}
              onUpdate={updateBulkOperation}
              isUpdating={isUpdating}
              receipts={receipts}
            />
          </div>

          {/* Receipts Section */}
          <BulkOperationDetailsReceipts
            bulkOperationId={bulkOperationId || ''}
            receipts={receipts}
            receiptsLoading={receiptsLoading}
            onRemoveReceipt={removeReceipt}
            removingReceiptIds={removingReceiptIds}
            onUploadingStateChange={setHasUploadingFiles}
            hasUploadingFiles={hasUploadingFiles}
          />
        </div>
      </div>
    </AppLayout>
  );
};

export default BulkOperationDetailsPage;
